/**
 * Edit-theater event handling shared by the chat and commentary SSE paths.
 *
 * Token deltas build the live draft; edit-start snaps it to the server's
 * baseline; edit/check/receipt accumulate the show onto the message. The
 * draft component plays them; complete-application awaits `theaterDone` so
 * the typeset never cuts the show mid-act.
 */

import type { ChatEvent } from '@skjv/common';
import type { ChatMessage } from './types';

/** Message-field patch for a streaming/theater event, or null if the event
 *  is not one of ours. */
export function theaterEventPatch(
  msg: ChatMessage,
  event: ChatEvent,
): Partial<ChatMessage> | null {
  if (event.type === 'token') {
    return {
      streamingText: (msg.streamingText ?? '') + event.text,
      streamingTokens: (msg.streamingTokens ?? 0) + 1,
    };
  }
  if (event.type === 'token-reset') {
    return { streamingText: '', streamingTokens: 0 };
  }
  if (event.type === 'edit-start') {
    // Curtain-up: silently reconcile the draft to the server baseline
    // (drift guardrail — the show must never corrupt the commentary).
    return { editBaseline: event.text, streamingText: event.text };
  }
  if (event.type === 'edit') {
    return {
      editOps: [
        ...(msg.editOps ?? []),
        {
          op: event.op,
          act: event.act,
          start: event.start,
          end: event.end,
          text: event.text,
          reason: event.reason,
        },
      ],
    };
  }
  if (event.type === 'check') {
    const checks = [...(msg.editChecks ?? [])];
    const existing = checks.findIndex((c) => c.act === event.act);
    const entry = { act: event.act, label: event.label, status: event.status };
    if (existing >= 0) checks[existing] = entry;
    else checks.push(entry);
    return { editChecks: checks };
  }
  if (event.type === 'receipt') {
    return { editReceipt: event.items };
  }
  return null;
}

/** Resolve once the message's show finished playing (or immediately when
 *  no show started). Safety timeout keeps a stuck animation from ever
 *  blocking the ground-truth typeset. */
export async function waitForTheaterDone(
  messages: ChatMessage[],
  messageId: string,
  timeoutMs = 16_000,
): Promise<void> {
  const t0 = Date.now();
  for (;;) {
    const msg = messages.find((m) => m.id === messageId);
    if (!msg || !msg.editBaseline || msg.theaterDone) return;
    if (Date.now() - t0 > timeoutMs) return;
    await new Promise((r) => setTimeout(r, 120));
  }
}
